import { useState, useRef, useEffect } from 'react'
import { useStore } from 'react-redux'
import { toDateId, toMonthId, startOfMonth } from '@calendly/datetime'

import { monthSelector, timezoneSelector } from 'selectors'
import { canProcessRangesInParallel } from 'selectors/event_type'
import { eventTypeSelector } from 'root-api'
import { buildRanges, fetchAvailabilityTracings, runSequentially } from 'services/calendar'
import { downloadJSON } from 'utils/download'

export const useDownloadBookingTracing = () => {
  const store = useStore()
  const [isDownloading, setIsDownloading] = useState(false)
  const [error, setError] = useState(null)
  const isMountedRef = useRef(true)

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const downloadTracing = async () => {
    if (!isMountedRef.current) {
      return { success: false, error: 'Component unmounted' }
    }

    const state = store.getState()
    const month = monthSelector(state)
    const timezone = timezoneSelector(state)
    const eventType = eventTypeSelector(state)

    if (!month || !month.isValid) {
      const message = 'Calendar month is not available. Please wait for the calendar to load or select a date.'
      if (isMountedRef.current) {
        setError(message)
      }
      return { success: false, error: new Error(message) }
    }

    if (isMountedRef.current) {
      setIsDownloading(true)
      setError(null)
    }

    try {
      // Build date ranges for the current month (normalize to first day like actions/calendar.js)
      const monthId = toDateId(startOfMonth(month))
      const ranges = buildRanges(
        monthId,
        eventType.multi_publisher,
        true, // Always enable diagnostics for tracing
        timezone
      )

      // Respect calendar provider restrictions (e.g. Microsoft) - use sequential when parallel is not allowed
      const results = canProcessRangesInParallel(state)
        ? await Promise.all(ranges.map((range) => fetchAvailabilityTracings(state, range)))
        : await runSequentially(ranges, (range) => fetchAvailabilityTracings(state, range))

      // Prepare the tracing data
      const tracingData = {
        eventType: {
          uuid: eventType.uuid,
          name: eventType.name
        },
        month: toMonthId(month),
        timezone,
        ranges,
        results: results.map(([success, data]) => ({
          success,
          data: success ? data : null,
          error: !success ? data : null
        }))
      }

      // Download as JSON file
      const filename = 'booking-tracing.json'
      downloadJSON(tracingData, filename)

      return { success: true }
    } catch (err) {
      console.error('Error downloading booking tracing:', err)
      if (isMountedRef.current) {
        setError(err.message || 'Failed to download tracing data')
      }
      return { success: false, error: err }
    } finally {
      if (isMountedRef.current) {
        setIsDownloading(false)
      }
    }
  }

  return {
    downloadTracing,
    isDownloading,
    error
  }
}
